import React, { useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { format } from "timeago.js";

const SocialProofToast = ({ user }) => {
  const toastIdRef = useRef(null);
  const intervalRef = useRef(null);
  const isPricingPage = window.location.pathname === "/pricing";

  const formatToolName = (name) =>
    name
      .replace(/_/g, " ")
      .replace(
        /\w\S*/g,
        (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );

  const getCountryFlag = (countryCode) =>
    countryCode
      ? countryCode
          .toUpperCase()
          .replace(/./g, (char) =>
            String.fromCodePoint(char.charCodeAt(0) + 127397)
          )
      : "";

  const actions = [
    "boosted their workflow",
    "saved time",
    "got better results",
    "optimized productivity",
    "discovered new features",
    "streamlined their process",
    "achieved their goals",
  ];

  const ctaOptions = [
    {
      text: "🚀 Join Them Now",
      style: {
        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
        hoverBackground: "linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)",
      },
    },
    {
      text: "🔓 Unlock Pro",
      style: {
        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        hoverBackground: "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
      },
    },
    {
      text: "⚡ Boost Productivity",
      style: {
        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
        hoverBackground: "linear-gradient(135deg, #047857 0%, #065f46 100%)",
      },
    },
    {
      text: "✨ Get Access",
      style: {
        background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
        hoverBackground: "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
      },
    },
    {
      text: "💎 Try Premium",
      style: {
        background: "linear-gradient(135deg, #db2777 0%, #be185d 100%)",
        hoverBackground: "linear-gradient(135deg, #be185d 0%, #9d174d 100%)",
      },
    },
  ];

  const messageTemplates = [
    ({ toolName }) => (
      <>
        and many creators trust <strong>{toolName}</strong>
      </>
    ),
    ({ toolName }) => (
      <>
        just unlocked value with <strong>{toolName}</strong>
      </>
    ),
    ({ toolName }) => (
      <>
        made life easier using <strong>{toolName}</strong>
      </>
    ),
    ({ toolName }) => (
      <>
        and others boosted output with <strong>{toolName}</strong>
      </>
    ),
    ({ toolName }) => (
      <>
        and other professionals rely on <strong>{toolName}</strong>
      </>
    ),
  ];

  const generateMessage = ({ toolName }) => {
    const template =
      messageTemplates[Math.floor(Math.random() * messageTemplates.length)];
    return template({ toolName });
  };

  useEffect(() => {
    if (isPricingPage) return;

    let index = 0;
    let activities = [];

    const fetchActivities = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/activity/recent-activities`
        );
        activities =
          res.data.activities?.filter(
            (activity) =>
              activity.userName?.toLowerCase() !== user?.name?.toLowerCase()
          ) || [];

        if (!activities.length) return;
        showNextToast();
        intervalRef.current = setInterval(showNextToast, 20000);
      } catch (err) {
        console.error("Failed to fetch social proof activities:", err.message);
      }
    };

    const showNextToast = () => {
      if (
        document.hidden ||
        !activities.length ||
        window.location.pathname === "/pricing"
      )
        return;

      const activity = activities[index % activities.length];
      const { userName, country, toolName, timestamp } = activity;

      const displayName = userName || "A user";
      const flag = getCountryFlag(country);
      const formattedToolName = formatToolName(toolName);
      const relativeTime = timestamp ? format(timestamp) : "just now";
      const message = generateMessage({ toolName: formattedToolName });

      const ctaPool = [...ctaOptions, ctaOptions[0], ctaOptions[1]];
      const selectedCta = ctaPool[Math.floor(Math.random() * ctaPool.length)];

      if (toastIdRef.current !== null) {
        toast.dismiss(toastIdRef.current);
      }

      toastIdRef.current = toast.info(
        <div style={{ marginRight: 20 }}>
          <strong>🚀 {displayName}</strong>{" "}
          {flag && (
            <>
              from <strong>{flag}</strong>{" "}
            </>
          )}
          {message}
          <br />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "12px",
            }}
          >
            <small
              style={{
                fontSize: "0.75rem",
                color: "#f8fafc", // Lighter text for better contrast
                fontStyle: "italic",
              }}
            >
              {relativeTime}
            </small>
            <a
              href="https://bit.ly/unlock-pro-access"
              style={{
                background: selectedCta.style.background,
                color: "white",
                padding: "8px 16px",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "0.85rem",
                textDecoration: "none",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                border: "none",
                cursor: "pointer",
                minWidth: "160px",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = selectedCta.style.hoverBackground;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = selectedCta.style.background;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
              }}
              onClick={() => {
                if (window.analytics) {
                  window.analytics.track("SocialProofCTA_Click", {
                    cta_text: selectedCta.text,
                    user_name: displayName,
                    tool_name: formattedToolName,
                  });
                }
              }}
            >
              <span>{selectedCta.text}</span>
            </a>
          </div>
        </div>,
        {
          position: "bottom-left",
          autoClose: 8000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: false,
          style: {
            background: "#1e293b", // slate-800 - Dark blue-gray background
            color: "#f8fafc", // slate-50 - Light text for contrast
            borderLeft: "5px solid #4f46e5", // indigo-600
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
            maxWidth: "420px",
            marginBottom: "20px",
            border: "1px solid #334155", // slate-700 border
          },
          icon: false,
        }
      );

      index++;
    };

    fetchActivities();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (toastIdRef.current !== null) toast.dismiss(toastIdRef.current);
    };
  }, [isPricingPage, user]);

  return isPricingPage ? null : (
    <ToastContainer position="bottom-left" newestOnTop limit={1} />
  );
};

export default SocialProofToast;
